import { effectivePermissionSetHas, type UserRole } from '@utazzo/shared';

/**
 * Website CMS write permissions. Exhaustive - adding a new capability requires
 * (a) a new union member, (b) a granular-code mapping and/or matrix row,
 * (c) a passing row in rbac.test.ts.
 *
 * Primary gate: GET /me `effectivePermissions` (ADR-0056). The role matrix
 * below is a fallback for capabilities with no granular code (e.g. media)
 * and while the profile query is still loading.
 */
export type WebsitePermission =
  | 'pages.write'
  | 'pages.publish'
  | 'pages.delete'
  // 2026-08-04. The Careers screen is a CUSTOM WebsitePage rendered through
  // PageEditor, but its sidebar item + route are gated on `web.career.*`
  // (ADR-0063). Without its own capability the editor fell back to
  // `pages.write`, so an HR user granted web.career.write got the page
  // read-only. See CAREER_PAGE_CONFIG in page-screen-config.ts.
  | 'career.write'
  | 'career.publish'
  | 'career.delete'
  | 'tourism_boards.write'
  | 'tourism_boards.publish'
  | 'tourism_boards.delete'
  | 'landing_pages.write'
  | 'landing_pages.publish'
  | 'landing_pages.delete'
  | 'best_time_pages.write'
  | 'best_time_pages.publish'
  | 'best_time_pages.delete'
  | 'best_places_pages.write'
  | 'best_places_pages.publish'
  | 'best_places_pages.delete'
  | 'blogs.write'
  | 'blogs.publish'
  | 'blogs.delete'
  | 'sections.write'
  | 'sections.delete'
  | 'media.write'
  | 'media.delete'
  | 'navigation.write'
  | 'navigation.delete'
  | 'destinations.write'
  | 'destinations.delete'
  | 'partners.write'
  | 'partners.delete'
  | 'press.write'
  | 'press.delete'
  | 'settings.write';

export const WEBSITE_PERMISSIONS = [
  'pages.write',
  'pages.publish',
  'pages.delete',
  'career.write',
  'career.publish',
  'career.delete',
  'tourism_boards.write',
  'tourism_boards.publish',
  'tourism_boards.delete',
  'landing_pages.write',
  'landing_pages.publish',
  'landing_pages.delete',
  'best_time_pages.write',
  'best_time_pages.publish',
  'best_time_pages.delete',
  'best_places_pages.write',
  'best_places_pages.publish',
  'best_places_pages.delete',
  'blogs.write',
  'blogs.publish',
  'blogs.delete',
  'sections.write',
  'sections.delete',
  'media.write',
  'media.delete',
  'navigation.write',
  'navigation.delete',
  'destinations.write',
  'destinations.delete',
  'partners.write',
  'partners.delete',
  'press.write',
  'press.delete',
  'settings.write',
] as const satisfies readonly WebsitePermission[];

/**
 * Maps legacy website-CMS capabilities to granular permission codes from
 * `permissions-registry.ts`. When the signed-in user holds any listed code,
 * the action is allowed regardless of the role enum.
 */
export const WEBSITE_PERMISSION_GRANULAR_CODES: Readonly<
  Partial<Record<WebsitePermission, readonly string[]>>
> = {
  'pages.write': ['web.pages.write'],
  'pages.publish': ['web.pages.write'],
  'pages.delete': ['web.pages.delete'],
  // `web.pages.write` is accepted alongside `web.career.write` so the website
  // roles - which reached Careers through the Custom Pages gate before the
  // split - keep editing it unchanged.
  'career.write': ['web.career.write', 'web.pages.write'],
  // Publish + delete deliberately do NOT accept `web.career.*`. The Careers
  // page goes live through `POST /admin/website/pages/:id/publish`, which is
  // an all-pages endpoint restricted to the website roles - so a career-only
  // holder gets the button hidden rather than a 403 on click.
  'career.publish': ['web.pages.write'],
  'career.delete': ['web.pages.delete'],
  'tourism_boards.write': ['web.tourism_boards.write'],
  'tourism_boards.publish': ['web.tourism_boards.write'],
  'tourism_boards.delete': ['web.tourism_boards.delete'],
  'landing_pages.write': ['web.landing_pages.write'],
  'landing_pages.publish': ['web.landing_pages.write'],
  'landing_pages.delete': ['web.landing_pages.delete'],
  'best_time_pages.write': ['web.best_time_pages.write'],
  'best_time_pages.publish': ['web.best_time_pages.write'],
  'best_time_pages.delete': ['web.best_time_pages.delete'],
  'best_places_pages.write': ['web.best_places_pages.write'],
  'best_places_pages.publish': ['web.best_places_pages.write'],
  'best_places_pages.delete': ['web.best_places_pages.delete'],
  'blogs.write': ['web.blogs.write'],
  'blogs.publish': ['web.blogs.write'],
  'blogs.delete': ['web.blogs.delete'],
  'sections.write': ['web.pages.write'],
  'sections.delete': ['web.pages.write'],
  'navigation.write': ['web.navigation.write'],
  'navigation.delete': ['web.navigation.write'],
  'destinations.write': ['inv.destinations.write', 'web.destination_pages.write'],
  'destinations.delete': ['web.destination_pages.delete'],
};

/**
 * Table-driven role matrix. Roles not listed for a permission cannot do it
 * unless they hold a matching granular code (see `canWebsiteAction`).
 */
const PERMISSION_MATRIX: Record<WebsitePermission, readonly UserRole[]> = {
  'pages.write': ['ADMIN', 'MARKETING_LEAD', 'CONTENT_EDITOR', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'pages.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'pages.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  // Mirrors the pages.* rows: the HR desk reaches Careers via its
  // `web.career.*` grants (checked first in canWebsiteAction), not via a role.
  'career.write': ['ADMIN', 'MARKETING_LEAD', 'CONTENT_EDITOR', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'career.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'career.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'tourism_boards.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'tourism_boards.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'tourism_boards.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'landing_pages.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'landing_pages.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'landing_pages.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'best_time_pages.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'best_time_pages.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'best_time_pages.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'best_places_pages.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'best_places_pages.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'best_places_pages.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'blogs.write': ['ADMIN', 'MARKETING_LEAD', 'CONTENT_EDITOR', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'blogs.publish': ['ADMIN', 'MARKETING_LEAD', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'blogs.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'sections.write': ['ADMIN', 'MARKETING_LEAD', 'CONTENT_EDITOR', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'sections.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'media.write': ['ADMIN', 'MARKETING_LEAD', 'CONTENT_EDITOR', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'media.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'navigation.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'navigation.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'destinations.write': [
    'ADMIN',
    'MARKETING_LEAD',
    'CONTENT_EDITOR',
    'WEBSITE_MANAGER',
    'WEBSITE_SEO',
  ],
  'destinations.delete': ['ADMIN', 'WEBSITE_MANAGER', 'WEBSITE_SEO'],
  'partners.write': ['ADMIN', 'MARKETING_LEAD'],
  'partners.delete': ['ADMIN'],
  'press.write': ['ADMIN', 'MARKETING_LEAD'],
  'press.delete': ['ADMIN'],
  'settings.write': ['ADMIN', 'MARKETING_LEAD'],
};

/** Role-only fallback used by `canWebsiteAction` and legacy call sites. */
export function canRoleDo(role: UserRole, action: WebsitePermission): boolean {
  const allowed = PERMISSION_MATRIX[action];
  if (!allowed) return false;
  return allowed.includes(role);
}

/**
 * Website CMS capability check. Prefers granular codes from GET /me; falls
 * back to the role matrix when codes are absent or still loading.
 */
export function canWebsiteAction(
  role: UserRole | null,
  action: WebsitePermission,
  effectivePermissions?: Set<string> | null | undefined,
): boolean {
  if (role === null) return false;

  const codes = WEBSITE_PERMISSION_GRANULAR_CODES[action];
  if (codes && effectivePermissions instanceof Set) {
    if (codes.some((code) => effectivePermissionSetHas(effectivePermissions, code))) {
      return true;
    }
  }

  return canRoleDo(role, action);
}
